import { can, PortalPermissions } from "../../config/permissions";
import React, {
  useReducer,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  EyeOutlined,
  UsergroupAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import Table, { TableColumn } from '../../components/Table';
import LoadingOverlay from '../../components/LoadingOverlay';
import { Reducer, InitialState } from './actions/reducer';
import { createFormatProvider } from '../../services/providers/factories';
import ViewUserModal from './components/viewUserModal';
import FilterForm from './components/FilterForm';
import { IFilterUserProps } from './types';
import { formatterPhoneNumber } from '../../components/Masks';
import {
  getAllUsers,
  onViewUserModalStatus,
  linkUser,
  getUserInformation,
  desvincularUsuario,
} from './actions';
import {
  Container, ResponsiveTable,
  TableContainer,
  ResponsiveRow,
  ResponsiveColumn,
  ResponsiveTableTitle,
} from './styles';
import { T2 } from '../../styles/titles';
import Modal from '../../components/Modal';
import LinkUserForm from './components/linkUserFilter';
import { IFormValues } from './components/linkUserFilter/types';
import useGetMe from "../../hooks/useCurrentUser";

// Tela consultiva: criar, editar e excluir usuário saíram para o painel do VAULT,
// que é onde a credencial passou a viver. Aqui ficam a consulta, o detalhe do
// usuário — de onde sai o acesso de suporte — e o vínculo com antecipadora, que
// é produto e não identidade.
const Usuarios: React.FC = () => {
  const [state, dispatch] = useReducer(Reducer, InitialState);
  const [pageSize, setPageSize] = useState<number>(20);
  const [offSet] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterOrder, setFilterOrder] = useState<any>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [userIdToLink, setUserIdToLink] = useState<string>('');
  const user = useGetMe();

  const fetchAllUsers = useCallback(() => getAllUsers(0, 5000)(dispatch), []);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const handleViewModalStatus = (status: boolean) => onViewUserModalStatus(status)(dispatch);

  const fetchUserInformation = useCallback((userId: string) => getUserInformation(userId)(dispatch), []);

  const formatProvider = useMemo(() => createFormatProvider(), []);
  const formattedDate = (date: string) => formatProvider.formatDate(Date.parse(date));
  const formatUserType = (user: number) => {
    if (user === 1) {
      return 'Pessoa Física';
    }
    if (user === 2) {
      return 'Pessoa Jurídica';
    }
    if (user === 3) {
      return 'Administrador'
    }
    if (user === 4) {
      return 'Antecipadora'
    }
    if (user === 6) {
      return 'Comercial / Parceiro Facilitador'
    }
    if (user === 7) {
      return 'White Label'
    }
    if (user === 8) {
      return 'Conta Split'
    }
    return '';
  };

  const responsiveFormartUserType = (user: number):string => {
    switch (user) {
      case 1: return 'PF';
      case 2: return 'PJ';
      case 3: return 'Administrador';
      case 4: return 'Antecipadora';
      case 6: return 'Comercial / Parceiro Facilitador';
      case 7: return 'White Label';
      case 8: return 'Conta Split';
      default: return '';
    }
  }

  const fetchAllUsersPagination = (initialPage: number, finalPage: number) => {
    getAllUsers(initialPage, finalPage, filterOrder)(dispatch);
  };

  const handleDesvincularUsuario = async (id: string) => {
    await desvincularUsuario(id)(dispatch);
    fetchAllUsers();
  };

  const closeModal = () => {
    setModalVisible(false);
  }

  const handleUserFilter = async (filter: IFilterUserProps) => {
    setFilterOrder(filter);
    getAllUsers(offSet, pageSize, filter)(dispatch);
  };

  const handleLinkUser = async (values : IFormValues) => {
    linkUser(userIdToLink, values.anticipationId?.value)(dispatch)
    closeModal();
    fetchAllUsers();
  };
  useEffect(() => {
    if (currentPage > 1) {
      fetchAllUsersPagination(pageSize * (currentPage - 1), (currentPage * pageSize))
    }
    if (currentPage === 1) {
      fetchAllUsersPagination(0, (currentPage * pageSize));
    }
  }, [currentPage, pageSize]);

  // Remove the logic for pagination for the responsive table
  const userList = state.users.records.map((user) => ({
    ...user,
    cidade: user.address?.cidade || 'Não Cadastrado',
  }));

  return (
    <Container>
      <Modal
        title="Vincular Usuário"
        centered
        visible={modalVisible}
        onCancel={closeModal}
        width={400}
      >
        <LinkUserForm onSubmit={handleLinkUser} />
      </Modal>
      <LoadingOverlay
        show={state.isViewUserModalOpen ? false : state.loading}
        relative
      />
      <T2>Usuários</T2>
      <FilterForm
        onFormSubmit={handleUserFilter}
      />
      <TableContainer>
        <Table
          dataSource={[...state.users.records]}
          pagination={
            {
              showSizeChanger: true,
              onShowSizeChange: (current: number, size: number) => {
                setPageSize(size);
              },
              defaultPageSize: 20,
              total: state.users.count,
              onChange: (pageNum) => {
                setCurrentPage(pageNum);
              },
            }
          }
        >
          <TableColumn title="Nome" dataIndex="name" key="name" />
          <TableColumn title="Sobrenome" dataIndex="lastName" key="lastName" />
          <TableColumn title="E-Mail" dataIndex="email" key="email" />
          <TableColumn title="Tipo de usuário" dataIndex="userType" key="userType" render={formatUserType} />
          <TableColumn title="Data de cadastro" dataIndex="createdAt" key="createdAt" render={formattedDate} />
          <TableColumn title="Telefone" dataIndex="phoneNumber" key="phoneNumber" render={formatterPhoneNumber} />
          <TableColumn
            title="Visualizar"
            dataIndex="visualizar"
            key="visualizar"
            render={(_: any, record: any) => (state.users.records.length >= 1 ? (
              <EyeOutlined onClick={() => fetchUserInformation(record.id)} />
            ) : null)}
          />
          { user.userType === 3 &&(
            <TableColumn
              title="Vincular usuário"
              dataIndex="vincularUsuario"
              key="vincularUsuario"
              render={(i: any, e: any) => (e.travaAntecipadora || e.userType === 3 || e.userType === 4 ? (
                can(PortalPermissions.administracao.usuarios.desvincularAntecipadora) ? (
                  <UserDeleteOutlined
                    style={{ color: 'red' }}
                    onClick={() => handleDesvincularUsuario(e.id)}
                  />
                ) : null
              ) : (
                can(PortalPermissions.administracao.usuarios.vincularAntecipadora) ? (
                  <UsergroupAddOutlined
                    style={{ color: 'green' }}
                    onClick={() => { setModalVisible(true); setUserIdToLink(e.id) }}
                  />
                ) : null
              ))}
            />
          )}
        </Table>
      </TableContainer>

      <ResponsiveTable>
        <ResponsiveRow style={{ borderBottom: '1px solid gray', borderRadius: '0px'}}>
          <ResponsiveTableTitle>Nome</ResponsiveTableTitle>
          <ResponsiveTableTitle>Empresa</ResponsiveTableTitle>
          <ResponsiveTableTitle>Tipo</ResponsiveTableTitle>
        </ResponsiveRow>
        {userList.map((user) => (
          <ResponsiveRow
            key={user.id}
            style={{ backgroundColor: "white", boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.2)", cursor: "pointer" }}
            onClick={() => fetchUserInformation(user.id)}
          >
            <ResponsiveColumn>
              <p>{user.name}</p>
            </ResponsiveColumn>
            <ResponsiveColumn>
              <p>{user.companyName}</p>
            </ResponsiveColumn>
            <ResponsiveColumn>
              <p><strong>{responsiveFormartUserType(user.userType)}</strong></p>
            </ResponsiveColumn>
          </ResponsiveRow>
        ))}
      </ResponsiveTable>
      <ViewUserModal
        user={{ ...state.user }}
        isVisible={state.isViewUserModalOpen}
        onClose={() => handleViewModalStatus(false)}
        loading={state.loading}
      />
    </Container>
  );
};

export default Usuarios;
